import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  ListingCollectionErrorCodes,
  ListingCollectionErrorEnvelope,
  CreateListingCollectionPayload,
  UpdateListingCollectionPayload,
  RawListingCollection,
} from "../../interfaces/ListingCollectionObject";
import {
  convertRawListingCollection,
  convertRawListingCollectionList,
  convertRawListingCollectionDetail,
} from "../converters/backendToUi/listingCollectionConvertor";
import {
  buildCreateListingCollectionPayload,
  buildUpdateListingCollectionPayload,
} from "../converters/uiToBackend/ListingCollectionConvertor";
import {
  extractApiErrorMessage as extractErrorMessage,
  fetchOrThrowFriendly,
} from "../helpers/ApiErrorHelper";
import { getRequest, postRequest, putRequest, deleteRequest } from "../http/httpRequest";

/**
 * Listing-collection error envelope. These endpoints report failures inside a
 * 2xx body ({errorInformation:[{errorCode}], generalProcessingStatus}), so a
 * response.ok check alone isn't enough. Codes: see ListingCollectionErrorCodes.
 */
function throwOnCollectionFailure(data: unknown, fallback: string): void {
  if (!data || typeof data !== "object") return;
  const envelope = data as ListingCollectionErrorEnvelope;

  const info = Array.isArray(envelope.errorInformation)
    ? envelope.errorInformation
    : [];
  const status = envelope.generalProcessingStatus?.processingStatus;
  const failed =
    info.length > 0 ||
    (typeof status === "string" && !["OK", "SUCCESS"].includes(status.toUpperCase()));
  if (!failed) return;

  const code = info[0]?.errorCode;
  switch (code) {
    case ListingCollectionErrorCodes.NOT_FOUND:
      throw collectionError("We couldn't find that collection.", code);
    case ListingCollectionErrorCodes.VALIDATION:
      throw collectionError("Invalid request — provide a name or creator.", code);
    case ListingCollectionErrorCodes.NAME_REQUIRED:
      throw collectionError("A collection name is required.", code);
    case ListingCollectionErrorCodes.LISTING_IDS_REQUIRED:
      throw collectionError("Add at least one listing to the collection.", code);
    case ListingCollectionErrorCodes.UNAUTHENTICATED:
      throw collectionError("You must be signed in to do that.", code);
    case ListingCollectionErrorCodes.INTERNAL:
      throw collectionError(
        "Something went wrong on our end. Please try again.",
        code
      );
    default:
      throw collectionError(extractErrorMessage(data) || fallback, code);
  }
}

/** Errors carry `code` through Redux Toolkit's miniSerializeError so
 *  `.unwrap()` callers can branch on it. */
function collectionError(message: string, code?: string): Error {
  const err = new Error(message);
  if (code) (err as Error & { code?: string }).code = code;
  return err;
}

async function statusToMessage(response: Response, fallback: string): Promise<string> {
  if (response.status === 403) return "You don't have permission to do this.";
  if (response.status === 404) return "We couldn't find that collection.";
  if (response.status >= 500) return "Something went wrong on our end. Please try again.";
  const data = await response.json().catch(() => null);
  return extractErrorMessage(data) || fallback;
}

/** `contents` on these endpoints is a single-element array on success. */
function coerceContentsSingle(data: unknown): Record<string, unknown> | null {
  const contents = (data as { contents?: unknown[] } | null)?.contents;
  const first = Array.isArray(contents) ? contents[0] : null;
  return first && typeof first === "object"
    ? (first as Record<string, unknown>)
    : null;
}

// Collections matching a name (partial match handled by the backend).
async function searchListingCollectionsByName(collectionName: string) {
  const endpoint =
    `${ServiceEndpoints.LISTING_COLLECTIONS_BASE}?name=` +
    encodeURIComponent(collectionName);
  const response = await fetchOrThrowFriendly(() =>
    getRequest(endpoint, false)
  );
  if (!response.ok) {
    throw new Error(
      await statusToMessage(response, "Couldn't search collections")
    );
  }
  const data = await response.json();
  throwOnCollectionFailure(data, "Couldn't search collections");
  return convertRawListingCollectionList(data);
}

/**
 * One collection plus its members in a single call.
 *
 * `includeListingDetails=false` returns uid-only stubs; `true` returns full
 * product records. The public page uses `true`; the admin list uses `false`.
 */
async function getListingCollectionById(
  collectionId: string,
  includeListingDetails = true
) {
  const endpoint =
    `${ServiceEndpoints.LISTING_COLLECTIONS_BASE}/` +
    encodeURIComponent(collectionId) +
    "?includeListingDetails=" +
    String(includeListingDetails);
  const response = await fetchOrThrowFriendly(() =>
    getRequest(endpoint, false)
  );
  if (!response.ok) {
    if (response.status === 404) {
      throw collectionError(
        "We couldn't find that collection.",
        ListingCollectionErrorCodes.NOT_FOUND
      );
    }
    throw new Error(
      await statusToMessage(response, "Couldn't load this collection")
    );
  }
  const data = await response.json();
  throwOnCollectionFailure(data, "Couldn't load this collection");

  const detail = convertRawListingCollectionDetail(data);
  if (!detail) {
    throw collectionError(
      "We couldn't find that collection.",
      ListingCollectionErrorCodes.NOT_FOUND
    );
  }
  return detail;
}

/**
 * A curator's own collections — `creatorId` filter on the same base endpoint.
 * Bearer token identifies the caller, but `creatorId` is still sent explicitly.
 */
async function getMyListingCollections(creatorId: string) {
  const endpoint =
    `${ServiceEndpoints.LISTING_COLLECTIONS_BASE}?creatorId=` +
    encodeURIComponent(creatorId);
  const response = await fetchOrThrowFriendly(() =>
    getRequest(endpoint, true)
  );
  if (!response.ok) {
    throw new Error(
      await statusToMessage(response, "Couldn't load your collections")
    );
  }
  const data = await response.json();
  throwOnCollectionFailure(data, "Couldn't load your collections");
  return convertRawListingCollectionList(data);
}

async function createListingCollection(payload: CreateListingCollectionPayload) {
  const body = buildCreateListingCollectionPayload(payload);
  const response = await fetchOrThrowFriendly(() =>
    postRequest(ServiceEndpoints.LISTING_COLLECTIONS_BASE, body, true)
  );
  if (!response.ok) {
    throw new Error(
      await statusToMessage(response, "Couldn't create this collection")
    );
  }
  const data = await response.json();
  throwOnCollectionFailure(data, "Couldn't create this collection");
  const created = coerceContentsSingle(data);
  if (!created) {
    throw collectionError("Couldn't create this collection.");
  }
  return convertRawListingCollection(created as unknown as RawListingCollection);
}

async function updateListingCollection(payload: UpdateListingCollectionPayload) {
  const endpoint =
    `${ServiceEndpoints.LISTING_COLLECTIONS_BASE}/` +
    encodeURIComponent(payload.uid);
  const body = buildUpdateListingCollectionPayload(payload);
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => putRequest(endpoint, body, includeBearerToken));
  if (!response.ok) {
    throw new Error(
      await statusToMessage(response, "Couldn't update this collection")
    );
  }
  const data = await response.json();
  throwOnCollectionFailure(data, "Couldn't update this collection");
  const updated = coerceContentsSingle(data);
  if (!updated) {
    throw collectionError("Couldn't update this collection.");
  }
  return convertRawListingCollection(updated as unknown as RawListingCollection);
}

async function deleteListingCollection(collectionId: string): Promise<void> {
  const endpoint =
    `${ServiceEndpoints.LISTING_COLLECTIONS_BASE}/` +
    encodeURIComponent(collectionId);
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => deleteRequest(endpoint, includeBearerToken));
  if (!response.ok) {
    if (response.status === 404) {
      throw collectionError(
        "We couldn't find that collection.",
        ListingCollectionErrorCodes.NOT_FOUND
      );
    }
    throw new Error(
      await statusToMessage(response, "Couldn't delete this collection")
    );
  }
  const data = await response.json().catch(() => null);
  if (data) throwOnCollectionFailure(data, "Couldn't delete this collection");
}

const ListingCollectionApiService = {
  searchListingCollectionsByName,
  /** Alias used by NineSetupBulkActions: `{ name }` → searchListingCollectionsByName. */
  searchListingCollections: (rq: { name: string }) =>
    searchListingCollectionsByName(rq.name),
  getListingCollectionById,
  /** Alias used by NineSetupBulkActions: fetches with includeListingDetails=true. */
  getListingCollectionDetail: (collectionId: string) =>
    getListingCollectionById(collectionId, true),
  getMyListingCollections,
  createListingCollection,
  updateListingCollection,
  deleteListingCollection,
};

export default ListingCollectionApiService;
